import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";
import { COUNTRIES } from "@/lib/catalog";

export default function FarmProfileForm({ compact = false }) {
  const { user, refresh } = useAuth();
  const [states, setStates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    farm_name: user?.farm_name || "",
    street_address: user?.street_address || "",
    city: user?.city || "",
    state: user?.state || "",
    country: user?.country || "United States",
    phone: user?.phone || "",
    email: user?.email || "",
    fax: user?.fax || "",
    website: user?.website || "",
    services: user?.services || "",
    bio: user?.bio || "",
    profile_image_url: user?.profile_image_url || "",
    instagram: user?.instagram || "",
    facebook: user?.facebook || "",
    tiktok: user?.tiktok || "",
    youtube: user?.youtube || "",
    twitter: user?.twitter || "",
  });

  useEffect(() => { api.get("/states").then((r) => setStates(r.data)); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const save = async () => {
    if (!form.farm_name.trim()) {
      toast.error("Hobby farm name is required.");
      return;
    }
    setSaving(true);
    try {
      const { email, ...payload } = form; // email comes from Google auth, not editable
      await api.put("/auth/profile", payload);
      toast.success("Hobby farm profile saved. You're now in the directory.");
      refresh();
    } catch {
      toast.error("Could not save profile.");
    } finally {
      setSaving(false);
    }
  };

  const isComplete = user?.farm_name && user?.street_address && user?.city && user?.state && user?.phone;

  return (
    <div className="bg-white border border-stone-200 rounded-md p-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-semibold">Public Profile</div>
          <h2 className="mt-2 font-serif text-3xl text-stone-900">Your hobby farm details</h2>
          <p className="mt-2 text-sm text-stone-600 font-sans">
            This information appears on your <Link to={user ? `/hobby-farmers/${user.user_id}` : "/hobby-farmers"} className="text-emerald-800 underline">public profile</Link> so clients can find you.
          </p>
        </div>
        {isComplete && user?.user_id && (
          <Link to={`/hobby-farmers/${user.user_id}`} className="text-sm font-sans text-emerald-800 flex items-center gap-1 hover:underline">
            View public profile <ExternalLink size={12} />
          </Link>
        )}
      </div>

      <div className="mt-8 grid sm:grid-cols-2 gap-5">
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Hobby Farm Name *</label>
          <Input data-testid="farm-name" value={form.farm_name} onChange={set("farm_name")} placeholder="e.g., Greenfield Hollow Farm" className="mt-2 bg-stone-50" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Street Address</label>
          <Input data-testid="farm-address" value={form.street_address} onChange={set("street_address")} placeholder="1234 Country Road" className="mt-2 bg-stone-50" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">City</label>
          <Input data-testid="farm-city" value={form.city} onChange={set("city")} placeholder="Sonoma" className="mt-2 bg-stone-50" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Country</label>
          <Select value={form.country} onValueChange={set("country")}>
            <SelectTrigger data-testid="farm-country" className="mt-2 bg-stone-50"><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">State / Province / Region</label>
          {form.country === "United States" ? (
            <Select value={form.state} onValueChange={set("state")}>
              <SelectTrigger data-testid="farm-state" className="mt-2 bg-stone-50"><SelectValue placeholder="Select state" /></SelectTrigger>
              <SelectContent>{states.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          ) : (
            <Input data-testid="farm-state" value={form.state} onChange={set("state")} placeholder="Region / province" className="mt-2 bg-stone-50" />
          )}
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Phone</label>
          <Input data-testid="farm-phone" value={form.phone} onChange={set("phone")} placeholder="(707) 555-0142" className="mt-2 bg-stone-50" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Email</label>
          <Input data-testid="farm-email" value={form.email} disabled className="mt-2 bg-stone-100 text-stone-500" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Fax (optional)</label>
          <Input data-testid="farm-fax" value={form.fax} onChange={set("fax")} placeholder="Optional" className="mt-2 bg-stone-50" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Website (optional)</label>
          <Input data-testid="farm-website" value={form.website} onChange={set("website")} placeholder="https://yourfarm.com" className="mt-2 bg-stone-50" />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">Products & Services</label>
          <Textarea
            data-testid="farm-services"
            value={form.services}
            onChange={set("services")}
            rows={3}
            placeholder="e.g., Pasture-raised eggs, grass-fed beef quarters, heirloom tomatoes May-October, raw honey, mutation produce free Saturdays"
            className="mt-2 bg-stone-50"
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold">About your hobby farm</label>
          <Textarea
            data-testid="farm-bio"
            value={form.bio}
            onChange={set("bio")}
            rows={4}
            placeholder="Tell clients the story of your farm — what you grow, why you started, what they can expect when they order from you."
            className="mt-2 bg-stone-50"
          />
        </div>

        <div className="sm:col-span-2">
          <ImageUploader
            value={form.profile_image_url}
            onChange={(v) => setForm({ ...form, profile_image_url: v })}
            label="Profile photo (shows on your public farm page)"
            testid="farm-photo"
          />
        </div>

        <div className="sm:col-span-2 pt-2">
          <div className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3">Social Media (optional)</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Input data-testid="farm-instagram" value={form.instagram} onChange={set("instagram")} placeholder="Instagram handle (no @)" className="bg-stone-50" />
            <Input data-testid="farm-facebook" value={form.facebook} onChange={set("facebook")} placeholder="Facebook page name" className="bg-stone-50" />
            <Input data-testid="farm-tiktok" value={form.tiktok} onChange={set("tiktok")} placeholder="TikTok handle (no @)" className="bg-stone-50" />
            <Input data-testid="farm-youtube" value={form.youtube} onChange={set("youtube")} placeholder="YouTube @handle or channel" className="bg-stone-50" />
            <Input data-testid="farm-twitter" value={form.twitter} onChange={set("twitter")} placeholder="X / Twitter handle (no @)" className="bg-stone-50 sm:col-span-2" />
          </div>
        </div>
      </div>

      <Button data-testid="save-profile-btn" disabled={saving} onClick={save} className="mt-8 bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-7 py-6 rounded-md">
        {saving ? "Saving..." : "Save Profile"}
      </Button>
    </div>
  );
}
